import { useRef, useState, type CSSProperties } from "react";
import { Icon } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { t } from "@/lib/strings";

export interface MoveTargetParent {
  id: string;
  title: string;
}
export interface MoveTargetSection {
  /** Cell value to write onto the group-by column when promoting; null clears it. */
  value: unknown;
  label: string;
}

/**
 * Menu fallback for moving a sub-item (Task 4). A small hover control opens a
 * popover: pick a target parent entry (reparent) or "Als eigener Eintrag in
 * <Header>" (promote to top-level). Gives a reliable touch/keyboard path before
 * the drag lands (Tasks 5–6).
 *
 * The real `Popover` is anchor-driven (it portals + closes on outside click),
 * so this component owns the trigger ref + open state, exactly like ColumnMenu.
 */
export function MoveSubitemMenu({
  parents,
  sections,
  onReparent,
  onPromote,
}: {
  /** Top-level entries to reparent under (current parent already excluded). */
  parents: MoveTargetParent[];
  /** Headers to promote into, or null when the board has no group-by. */
  sections: MoveTargetSection[] | null;
  onReparent: (parentId: string) => void;
  onPromote: (section: MoveTargetSection | null) => void;
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={t.moveSubitemTitle}
        style={{
          width: 16,
          height: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: open ? "var(--ink-1)" : "var(--ink-3)",
          background: open ? "var(--surface-soft)" : "transparent",
          border: "none",
          borderRadius: 4,
        }}
      >
        <Icon name="cornerDownRight" size={13} />
      </button>
      {open ? (
        <Popover anchor={triggerRef.current} onClose={() => setOpen(false)} width={240}>
          <div style={{ padding: 4 }}>
            {parents.length ? <div style={sectionLabelStyle}>{t.moveUnderEntry}</div> : null}
            {parents.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onReparent(p.id);
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                style={rowStyle}
              >
                <Icon name="cornerDownRight" size={12} color="var(--ink-3)" />
                <span style={{ marginLeft: 6 }}>{p.title || t.titleEmpty}</span>
              </button>
            ))}
            {parents.length ? (
              <div style={{ height: 1, background: "var(--hairline)", margin: "4px 0" }} />
            ) : null}
            {sections ? (
              sections.map((s) => (
                <button
                  key={`${String(s.value)}::${s.label}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onPromote(s);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  style={rowStyle}
                >
                  {t.movePromotePrefix} {s.label}
                </button>
              ))
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onPromote(null);
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                style={rowStyle}
              >
                {t.movePromoteNoGroup}
              </button>
            )}
          </div>
        </Popover>
      ) : null}
    </>
  );
}

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  textAlign: "left",
  padding: "6px 8px",
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--ink-1)",
  fontSize: 13,
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const sectionLabelStyle: CSSProperties = {
  padding: "4px 8px 2px",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
